import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { get, isEmpty } from '../main/hooks/common';

interface UseEntityFilterProps {
  queryParams: Record<string, any>;
  defaultParams?: Record<string, any>;
  path?: string;
}

export const useEntityFilter = ({ queryParams, defaultParams, path }: UseEntityFilterProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [values, setValues] = useState<Record<string, any>>({});

  const usePath = path || pathname;
  const page = get(queryParams, 'page');

  const handleClear = () => {
    setValues({});
    navigate({
      pathname: usePath,
      search: new URLSearchParams(defaultParams).toString(),
    });
  };

  const handleSubmit = () => {
    let query = page === '1' ? { ...values } : { ...values, page: '1' };
    if (defaultParams) query = { ...query, ...defaultParams };
    if (page === '1') delete query['page'];

    navigate({
      pathname: usePath,
      search: !isEmpty(query) ? new URLSearchParams(query).toString() : '',
    });
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleChange = (value: string | number, name: string) => {
    setValues((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    handleChange(value, name);
  };

  const fieldDefaultProps = (name: string) => ({
    name,
  });

  const fieldProps = (name: string) => {
    return {
      ...fieldDefaultProps(name),
      onChange: handleInputChange,
      value: get(values, name, ''),
    };
  };

  const fieldSelectProps = (name: string) => {
    return {
      ...fieldDefaultProps(name),
      value: get(values, name, ''),
      onChange: handleInputChange,
    };
  };

  const fieldStrictProps = (name: string) => {
    return {
      ...fieldDefaultProps(name),
      value: get(values, name, ''),
      onChange: (value: string | null) => handleChange(value || '', name),
    };
  };

  const fieldDateProps = (name: string) => ({
    ...fieldDefaultProps(name),
    value: get(values, name, ''),
    onChange: (value: string | null) => handleChange(value || '', name),
  });

  useEffect(() => {
    setValues(queryParams);
  }, [queryParams]);

  return {
    values,
    setValues,
    handleClear,
    handleSubmit,
    handleFormSubmit,
    fieldProps,
    fieldSelectProps,
    fieldStrictProps,
    fieldDateProps,
  };
};
